// Konvo patch (konvo/chatwoot:4.12.1-jpegfix): WhatsApp rejects images over
// 5 MB in the dashboard. Instead of blocking them, re-encode PNG/JPEG images
// to JPEG in the browser so they fit under the limit.
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const CONVERTIBLE_TYPES = ['image/png', 'image/jpeg'];
const TARGET_SIZE = 4.8 * 1024 * 1024;
const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6];
const MAX_SIDE = 2560;

const isWhatsAppInbox = inbox => {
  const channelType = inbox?.channel_type;
  return (
    channelType === INBOX_TYPES.WHATSAPP ||
    (channelType === INBOX_TYPES.TWILIO && inbox?.medium === 'whatsapp')
  );
};

export const canConvertForWhatsApp = (inbox, file) =>
  isWhatsAppInbox(inbox) &&
  !file?.convertedForWhatsApp &&
  file?.file instanceof Blob &&
  CONVERTIBLE_TYPES.includes(file.file.type);

// Decodes the image applying its EXIF orientation, so phone photos are not
// rotated. createImageBitmap is preferred; <img> is the fallback (modern
// browsers apply EXIF orientation to it and to drawImage by default).
const loadImage = async blob => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, {
        imageOrientation: 'from-image',
      });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch (e) {
      // fall back to <img>
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
};

const toJpegBlob = (canvas, quality) =>
  new Promise(resolve => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });

// Draws on a white background (PNG transparency would turn black in JPEG) and
// tries each quality step; returns the first blob under TARGET_SIZE or null.
const encodeJpeg = async (image, width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image.source, 0, 0, width, height);
    // eslint-disable-next-line no-restricted-syntax
    for (const quality of QUALITY_STEPS) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await toJpegBlob(canvas, quality);
      if (blob && blob.size <= TARGET_SIZE) return blob;
    }
    return null;
  } catch (e) {
    // e.g. canvas too large for the browser: let the caller try smaller
    return null;
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
};

/**
 * Returns a copy of the upload object with its File re-encoded as JPEG, or
 * null if the conversion fails or cannot get under the size target.
 */
export const convertFileForWhatsApp = async file => {
  let image;
  try {
    image = await loadImage(file.file);
    const { width, height } = image;

    let blob = await encodeJpeg(image, width, height);
    const scale = MAX_SIDE / Math.max(width, height);
    if (!blob && scale < 1) {
      blob = await encodeJpeg(
        image,
        Math.round(width * scale),
        Math.round(height * scale)
      );
    }
    if (!blob) return null;

    const baseName = (file.name || file.file.name || 'image').replace(
      /\.[^.]+$/,
      ''
    );
    const jpeg = new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    return {
      ...file,
      file: jpeg,
      name: jpeg.name,
      type: jpeg.type,
      size: jpeg.size,
      convertedForWhatsApp: true,
    };
  } catch (e) {
    return null;
  } finally {
    image?.release();
  }
};
